import React, { useMemo } from 'react';
import { KeyMetricData } from 'src/types';
import { KeyMetricCard } from 'src/components/molecules/KeyMetricCard';
import { useKpiMetricLabels } from 'src/hooks/useKpiMetricLabels';
import { useKpiUnit } from 'src/hooks/useKpiUnit';
import { useMergeBenchmarkTimeseries } from 'src/hooks/useMergeBenchmarkTimeseries';
import { calculateTrendFromTimeSeries } from 'src/utils/calculateTrendFromTimeSeries';
import { useDADataContext } from '../../../contexts/DADataContext';
// eslint-disable-next-line no-restricted-imports
import { Icon as ChakraIcon } from '@chakra-ui/react';
import { TrendingUp } from 'lucide-react';

export const ConversionsOverTime: React.FC = () => {
  const { conversions, benchmarkConversions } = useDADataContext();
  const { getMergedTimeSeries } = useMergeBenchmarkTimeseries();

  const { isEuro } = useKpiUnit();
  const { metricLabel } = useKpiMetricLabels();

  const keyMetricData: KeyMetricData = useMemo(() => {
    let sum = 0;

    conversions.data.forEach((x) => {
      sum += x.measurements.modelledConversions;
    });

    const data = getMergedTimeSeries(
      conversions.data,
      benchmarkConversions.data,
      (v) => ({ value: v.measurements.modelledConversions }),
      (v) => ({ benchmark: v.measurements.modelledConversions }),
      false,
      'day',
    );

    return {
      name: `${metricLabel} over time`,
      data,
      isEuro,
      summaryValue: sum,
      isError: conversions.isError,
      isLoading: conversions.isLoading,
    };
  }, [
    conversions,
    isEuro,
    getMergedTimeSeries,
    benchmarkConversions,
    metricLabel,
  ]);

  const { trend, trendValue } = useMemo(() => {
    return calculateTrendFromTimeSeries(keyMetricData?.data ?? []);
  }, [keyMetricData]);

  return (
    <KeyMetricCard
      title={`${metricLabel} over time`}
      trend={trend}
      trendValue={trendValue}
      value={keyMetricData.summaryValue}
      isLoading={keyMetricData.isLoading}
      isError={keyMetricData.isError}
      icon={<ChakraIcon as={TrendingUp} boxSize={5} color="gray.500" />}
    />
  );
};
